/**
 * Utility to replace page paths with human-readable names in Bill's messages
 * Example: "/diary" → "<b>Diário</b>"
 */

// Mapping of page paths to display names
// ONLY includes authenticated pages - no onboarding/auth pages
const PAGE_NAMES: Record<string, string> = {
  // Main navigation
  '/menu': 'Menu',
  '/': 'Tela Inicial',
  
  // Diary / Sentimento section
  '/diary': 'Diário',
  '/diario-sentimento': 'Diário Emocional',
  '/diario-sentimento-categoria': 'Categorias de Sentimento',
  '/diario-sentimento-nota': 'Notas de Sentimento',
  '/add-note': 'Adicionar Nota',
  '/diario-nota': 'Nota do Diário',
  
  // Sobriety section
  '/sobriety-clock': 'Cronômetro de Sobriedade',
  '/sobriety-setup': 'Configuração de Sobriedade',
  
  // Meetings section
  '/encontros-online': 'Encontros',
  '/meetings': 'Reuniões',
  '/encontros': 'Encontros',
  
  // Profile & Settings
  '/perfil': 'Meu Perfil',
  '/profile': 'Perfil',
  '/change-password': 'Alterar Senha',
  '/change-email': 'Alterar Email',
  '/change-phone': 'Alterar Telefone',
  '/delete-account': 'Excluir Conta',
  
  // Bill chat
  '/bill': 'Conversar com Bill',
  '/chat': 'Chat',
};

/**
 * Replace page paths with formatted display names in text
 * Wraps page names in <b> tags for medium weight styling
 * 
 * @param text - Text that may contain page paths like "/diary"
 * @returns Text with paths replaced by formatted names like "<b>Diário</b>"
 * 
 * @example
 * formatPageNames("Você pode ir para /diary") 
 * // Returns: "Você pode ir para <b>Diário</b>"
 */
export function formatPageNames(text: string): string {
  if (!text) return text;
  
  let formattedText = text;
  
  // Sort paths by length (longest first) to avoid partial replacements
  // e.g., replace "/diario-sentimento-nota" before "/diario-sentimento"
  const sortedPaths = Object.keys(PAGE_NAMES).sort((a, b) => b.length - a.length);
  
  for (const path of sortedPaths) {
    const displayName = PAGE_NAMES[path];
    
    // Create regex to match the path (case-insensitive, word boundaries)
    // Handles variations like "/diary", " /diary ", "/diary."
    const pathRegex = new RegExp(`(^|\\s|[^a-zA-Z0-9])${path.replace(/\//g, '\\/')}(?=[\\s.,!?)]|$)`, 'gi');
    
    // Replace with formatted name
    formattedText = formattedText.replace(pathRegex, (_match, prefix) => {
      return `${prefix}<b>${displayName}</b>`;
    });
  }
  
  return formattedText;
}

/**
 * Get display name for a specific path
 * @param path - Page path like "/diary"
 * @returns Display name like "Diário" or the original path if not found
 */
export function getPageDisplayName(path: string): string {
  return PAGE_NAMES[path] || path;
}

/**
 * Check if a path has a display name mapping
 * @param path - Page path to check
 * @returns true if the path has a display name
 */
export function hasPageDisplayName(path: string): boolean {
  return path in PAGE_NAMES;
}
